import React, { useState } from "react";
import {
  BotOff,
  ChartNetwork,
  ChevronDown,
  EllipsisVertical,
  Fingerprint,
  Info,
  ListCheck,
  View,
  Plus,
  PlusIcon,
  Save,
  Trash2,
  Workflow,
  Logs,
  Zap,
  Database,
  Settings,
  Settings2,
} from "lucide-react";
import { Button } from "@components/ui/button";
import CustomTooltip from "@components/common/CustomTooltip";
import {
  codeNodes,
  customNodes,
  dataTransmission,
  logicNodes,
  lsNodes,
} from "./common/nodeTemplates";
import { workflowTypes } from "./common/commonData";
import { useNavigate } from "react-router-dom";
import { GeneralContext } from "@services/providers/GeneralProvider";
import FallBackUi from "../plugin/FallBackUi";

interface ConfigState {
  configurations: any[];
  isLoadingConfig: boolean;
}

interface WorkflowState {
  meshFlows: any[];
  activeMeshFlowUid: string | null;
  isLoadingWorkflows: boolean;
  loadingWorkflowUid: string | null;
  exportedMeshflows: any[];
  meshFlowToDelete: string | null;
}

interface LoadingState {
  isFetchingWorkflow: boolean;
  isDeletingWorkflow: boolean;
}

interface SidebarHandlers {
  onDragStart: (event: React.DragEvent, node: any) => void;
  onCreateMeshFlowClick: () => void;
  onMeshFlowSelect: (meshFlow: any) => void;
  onMeshFlowEdit: (meshFlow: any) => void;
  onMeshFlowDelete: (uid: string) => void;
  onExportedMeshflowDelete: (index: number) => void;
  onDeselect: () => void;
  onBackToUIPages: () => void;
}

interface MeshflowSidebarProps {
  configState: ConfigState;
  workflowState: WorkflowState;
  loadingState: LoadingState;
  handlers: SidebarHandlers;
}

const MeshflowSidebar: React.FC<MeshflowSidebarProps> = ({
  configState,
  workflowState,
  loadingState,
  handlers,
}) => {
  const { configurations, isLoadingConfig } = configState;
  const {
    meshFlows,
    activeMeshFlowUid,
    isLoadingWorkflows,
    loadingWorkflowUid,
    exportedMeshflows,
  } = workflowState;
  const { isFetchingWorkflow, isDeletingWorkflow } = loadingState;
  const {
    onDragStart,
    onCreateMeshFlowClick,
    onMeshFlowSelect,
    onMeshFlowEdit,
    onExportedMeshflowDelete,
    onDeselect,
    onBackToUIPages,
  } = handlers;
  const { isAdminUser } = React.useContext(GeneralContext);

  // Sort nodes alphabetically by header/title
  const sortedConfigurations = React.useMemo(() => {
    return [...configurations].sort((a, b) =>
      (a.title || "").localeCompare(b.title || ""),
    );
  }, [configurations]);

  const sortedCustomNodes = React.useMemo(() => {
    return [...customNodes].sort((a, b) =>
      (a.header || "").localeCompare(b.header || ""),
    );
  }, []);

  const sortedLsNodes = React.useMemo(() => {
    return [...lsNodes].sort((a, b) =>
      (a.header || "").localeCompare(b.header || ""),
    );
  }, []);

  const sortedDataTransmission = React.useMemo(() => {
    return [...dataTransmission].sort((a, b) =>
      (a.header || "").localeCompare(b.header || ""),
    );
  }, []);

  const sortedLogicNodes = React.useMemo(() => {
    return [...logicNodes].sort((a, b) =>
      (a.header || "").localeCompare(b.header || ""),
    );
  }, []);

  const iconMap: Record<string, any> = {
    save: Save,
  };

  const workflowLabel = (type: string) => {
    const workflow = workflowTypes.find((w: any) => w.value === type);
    return workflow ? workflow.label : "";
  };

  const getWorkflowIcon = (type: string) => {
    const normalizedType = String(type || "").toLowerCase();

    const workflowIconMap: Record<string, any> = {
      save_log: Logs,
      save_datalist: ListCheck,
      threatlookup: View,
      threat_lookup: View,
      action: Zap,
      settings: Settings,
      dspm: Workflow,
      itdr: Database,
      none: ChartNetwork,
    };

    return workflowIconMap[normalizedType] || ChartNetwork;
  };

  const sortedCodeNodes = React.useMemo(() => {
    return [...codeNodes].sort((a, b) =>
      (a.header || "").localeCompare(b.header || ""),
    );
  }, []);

  return (
    <div className="w-[260px] h-full border border-secondary rounded-lg bg-background flex flex-col overflow-hidden">
      <p className="text-2xl font-bold border-b p-5 flex items-center gap-2 flex-shrink-0">
        <Workflow size={25} className="text-primary" /> MeshConnectX
      </p>

      <div className="flex-1 min-h-0 p-1 pt-2 flex flex-col gap-4">
        <div className="bg-gray-50 border rounded-lg py-2 m-1 flex-[3] !shadow-sm min-h-0 overflow-x-hidden">
          {/* Configurations Section */}
          {isLoadingConfig ? (
            <div className="px-4 py-2 text-xs text-muted-foreground p-3 text-center">
              Loading configurations...
            </div>
          ) : (
            <NodeSection
              title="Configuration nodes"
              nodes={sortedConfigurations}
              onDragStart={onDragStart}
              isDisabled={!activeMeshFlowUid}
              isAdminUser={isAdminUser}
              showEmptyConfigFallback={true}
              defaultOpen={false}
              renderItem={(config: any, index: number) => {
                const nodeDescription =
                  "Users can configure and manage settings through the Config Node.";
                return (
                  <div
                    key={config.uid || index}
                    draggable
                    onDragStart={(e) =>
                      onDragStart(e, {
                        type: "authentication",
                        header: config.title || `Configuration ${index + 1}`,
                        borderColor: "var(--primary)",
                        shadowColor: "var(--light-red)",
                        icon: null,
                        iconStyle: "text-muted-foreground",
                        configData: config,
                        nodeDescription: nodeDescription,
                      })
                    }
                    className={`p-2 mr-1 px-3 rounded-lg bg-background mb-2 text-[10px] mt-2 flex items-center justify-between gap-2 cursor-move  ${!activeMeshFlowUid && meshFlows.length > 0 ? "shadow-[0_2px_6px_rgba(0,0,0,0.03)] text-gray-500" : "shadow-[0_2px_6px_rgba(0,0,0,0.12)]"}`}
                  >
                    <div className="flex items-center gap-2 font-semibold">
                      <Fingerprint
                        size={15}
                        className="text-muted-foreground"
                      />
                      <div>{config.title || `Configuration ${index + 1}`}</div>
                    </div>
                    <CustomTooltip text={nodeDescription} side="right">
                      <div className="cursor-help">
                        <Info
                          size={12}
                          className="text-muted-foreground hover:text-foreground transition"
                        />
                      </div>
                    </CustomTooltip>
                  </div>
                );
              }}
            />
          )}

          {/* Action Nodes */}
          <NodeSection
            title="Core Nodes"
            nodes={sortedCustomNodes}
            onDragStart={onDragStart}
            isDisabled={!activeMeshFlowUid}
            isAdminUser={isAdminUser}
          />

          {/* Linkshadow Nodes */}
          <NodeSection
            title="Linkshadow Nodes"
            nodes={sortedLsNodes}
            onDragStart={onDragStart}
            isDisabled={!activeMeshFlowUid}
            isAdminUser={isAdminUser}
          />

          {/* Data Transmission Nodes */}
          <NodeSection
            title="Data Transmission Nodes"
            nodes={sortedDataTransmission}
            onDragStart={onDragStart}
            isDisabled={!activeMeshFlowUid}
            isAdminUser={isAdminUser}
          />

          {/* Logic Nodes */}
          <NodeSection
            title="Logic Nodes"
            nodes={sortedLogicNodes}
            onDragStart={onDragStart}
            isDisabled={!activeMeshFlowUid}
            isAdminUser={isAdminUser}
          />

          {/* Code Nodes */}
          <NodeSection
            title="Code Nodes"
            nodes={sortedCodeNodes}
            onDragStart={onDragStart}
            isDisabled={!activeMeshFlowUid}
            isAdminUser={isAdminUser}
          />

          {/* Exported Meshflows */}
          {exportedMeshflows.length > 0 && (
            <NodeSection
              title={
                "Exported Node" + (exportedMeshflows.length > 1 ? `s` : "")
              }
              nodes={exportedMeshflows}
              onDragStart={onDragStart}
              isDisabled={!activeMeshFlowUid}
              isAdminUser={isAdminUser}
              renderItem={(node: any, index: number) => {
                const isDisabled = !activeMeshFlowUid;

                return (
                  <div
                    key={index}
                    draggable={!isDisabled}
                    onDragStart={(e) => {
                      if (!isDisabled) {
                        e.dataTransfer.setData(
                          "application/meshflow-data",
                          JSON.stringify(node.flowData),
                        );
                        e.dataTransfer.effectAllowed = "move";
                      }
                    }}
                    className="group p-2 px-3 rounded-lg bg-background font-semibold shadow-[0_2px_6px_rgba(0,0,0,0.05)] mb-2 text-[10px] mt-2 flex items-center justify-between gap-2 cursor-move active:cursor-grabbing"
                  >
                    <div className="flex items-center gap-2">
                      {node.icon &&
                        iconMap[node.icon] &&
                        React.createElement(iconMap[node.icon], {
                          size: 15,
                          className: "text-muted-foreground",
                        })}
                      <span className="text-foreground">{node.header}</span>
                    </div>

                    {/* Action Icons - Delete */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const exportedIndex = exportedMeshflows.findIndex(
                            (m: any) => m.flowData?.uid === node.flowData?.uid,
                          );
                          if (exportedIndex !== -1) {
                            onExportedMeshflowDelete(exportedIndex);
                          }
                        }}
                        className="text-primary hover:scale-110 transition-transform"
                        title="Delete Flow"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              }}
            />
          )}
        </div>

        {/* Saved Meshflows */}
        <div
          className={`py-2 rounded-md transition-all !shadow-sm backdrop-blur-md duration-1000 flex-[2] mb-3 min-h-0 flex flex-col mx-1 ${!activeMeshFlowUid && meshFlows.length > 0 ? "animate-pulse bg-muted border border-dashed rounded-xl border-primaryGreen backdrop-brightness-100" : ""} border rounded-xl`}
        >
          <button
            className={`w-full flex !px-3 items-center justify-between uppercase font-semibold text-xs transition-all duration-300 flex-shrink-0 border-b border-border !pb-2 ${!activeMeshFlowUid && meshFlows.length > 0 ? "text-primaryGreen " : "text-primaryGreen "}`}
          >
            <span className="flex items-center gap-2 ">
              Saved Mesh Flows
              {!isLoadingWorkflows && (
                <span
                  className={`h-4 w-4 text-background bg-primaryGreen rounded-full p-1 text-xs flex items-center justify-center`}
                >
                  {meshFlows.length}
                </span>
              )}
            </span>
            <div
              className="p-1 rounded-md bg-muted hover:bg-lightGray transition cursor-pointer"
              onClick={onCreateMeshFlowClick}
            >
              <Plus
                size={16}
                className="hover:text-primaryBlue cursor-pointer"
              />
            </div>
          </button>

          <div
            className={`overflow-hidden transition-all duration-300 mt-2 px-2 flex-1 min-h-0`}
          >
            {/* Mesh Flows List */}
            {isLoadingWorkflows ? (
              <div className="space-y-2 h-full overflow-y-auto overflow-x-hidden p-1">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="px-3 py-2 rounded-lg border mb-2 flex items-center justify-between gap-1 bg-background animate-pulse"
                  >
                    <div className="flex items-center gap-2 flex-1">
                      {/* Left icon skeleton */}
                      <div className="w-6 flex items-center justify-center">
                        <div className="h-6 w-6 rounded-full bg-lightGray" />
                      </div>

                      {/* Content section */}
                      <div className="flex items-center w-full justify-between">
                        <div className="flex flex-col w-[95px] gap-1">
                          {/* Title */}
                          <div className="h-3 w-20 rounded bg-lightGray" />

                          {/* Subtitle */}
                          <div className="flex items-center gap-2 mt-[2px]">
                            <div className="h-2 w-12 rounded bg-lightGray" />
                            <div className="h-2 w-8 rounded bg-lightGray" />
                          </div>
                        </div>

                        {/* Status badge skeleton */}
                        <div className="min-w-14 flex items-center justify-center">
                          <div className="h-4 w-14 rounded-lg bg-lightGray" />
                        </div>
                      </div>
                    </div>

                    {/* Right action button skeleton */}
                    <div className="flex items-center gap-1">
                      <div className="h-4 w-4 rounded bg-lightGray" />
                    </div>
                  </div>
                ))}
              </div>
            ) : meshFlows.length > 0 ? (
              <div
                className={`h-full overflow-y-auto overflow-x-hidden p-1 !scrollbar-thin !scrollbar-thumb-rounded !scrollbar-thumb-muted !scrollbar-track-transparent transition-all duration-300 font-sansBold `}
              >
                {meshFlows.map((meshFlow) =>
                  (() => {
                    const WorkflowIcon = getWorkflowIcon(
                      meshFlow.type_workflow,
                    );

                    return (
                      <div
                        key={meshFlow.uid}
                        className={`group px-3 py-2 rounded-lg font-semibold border mb-2 text-xs flex items-center justify-between gap-1 max-w-full ${
                          meshFlow.uid !== activeMeshFlowUid
                            ? "hover:bg-lightGray scale-[0.97] opacity-80 hover:opacity-100 hover:scale-[1] transition-all duration-200"
                            : "scale-[1] border-primaryGreen/60 bg-lightGreen shadow-md p-1 border-r-0 border-l-4 border-t-0 border-b-0"
                        } transition ${
                          isFetchingWorkflow || isDeletingWorkflow
                            ? "cursor-wait opacity-50"
                            : "cursor-pointer"
                        } ${
                          activeMeshFlowUid === meshFlow.uid
                            ? "border-l-4 border-l-primaryGreen"
                            : "bg-background"
                        }`}
                        onClick={() => {
                          if (!isFetchingWorkflow && !isDeletingWorkflow) {
                            if (meshFlow.uid === activeMeshFlowUid) {
                              onDeselect();
                            } else {
                              onMeshFlowSelect(meshFlow);
                            }
                          }
                        }}
                        title={meshFlow.title}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <div className="w-5">
                            {loadingWorkflowUid === meshFlow.uid &&
                            isFetchingWorkflow ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primaryGreen"></div>
                            ) : activeMeshFlowUid === meshFlow.uid ? (
                              <WorkflowIcon
                                className="text-primaryGreen"
                                size={18}
                              />
                            ) : (
                              <WorkflowIcon
                                className="text-muted-foreground"
                                size={18}
                              />
                            )}
                          </div>

                          {/*  status icon */}
                          <div className="flex items-center w-full justify-between">
                            <div className="flex flex-col w-[100px] mr-2">
                              <div className="font-semibold flex items-center gap-2 min-w-0 max-w-[90px]">
                                <span className="truncate block">
                                  {meshFlow.title}
                                </span>
                              </div>

                              <div className="text-[10px] text-muted-foreground flex items-center justify-between gap-2 mt-[2px] whitespace-nowrap max-full ">
                                <div>
                                  {workflowLabel(meshFlow.type_workflow)}
                                </div>
                                <div>
                                  <strong>. </strong>v{meshFlow.version}
                                </div>
                              </div>
                            </div>

                            {/* status */}
                            <div>
                              <div
                                className={`min-w-14 text-[10px] flex items-center justify-center gap-1 font-medium w-fit rounded-lg px-1.5 mt-1 
                               ${
                                 meshFlow.is_active &&
                                 meshFlow.uid === activeMeshFlowUid
                                   ? "text-networkGreen border-networkGreen border"
                                   : "text-muted-foreground bg-muted"
                               }`}
                              >
                                <div
                                  className={`w-1.5 h-1.5 rounded-full ${
                                    meshFlow.is_active &&
                                    meshFlow.uid === activeMeshFlowUid
                                      ? "bg-networkGreen"
                                      : "bg-muted-foreground"
                                  }`}
                                ></div>

                                <div>
                                  {meshFlow.is_active ? "Active " : "Inactive "}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {activeMeshFlowUid && meshFlows.length > 0 && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 w-fit">
                            <button
                              className="text-muted-foreground hover:scale-110 hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={(e) => {
                                e.stopPropagation();

                                if (!isDeletingWorkflow) {
                                  onMeshFlowEdit(meshFlow);
                                }
                              }}
                              disabled={isDeletingWorkflow}
                            >
                              <EllipsisVertical size={15} />
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })(),
                )}
              </div>
            ) : (
              <div className="border rounded-lg h-full flex items-center justify-center p-4 text-sm text-muted-foreground mb-2">
                <FallBackUi description="No mesh flows created yet." />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* return to general information */}
      <div
        className="flex-shrink-0 border-t hover:bg-muted transition cursor-pointer"
        onClick={onBackToUIPages}
      >
        <Button className="w-full flex items-center justify-center gap-2 bg-transparent text-muted-foreground rounded-none shadow-none">
          <Settings2 size={18} /> Back to UI Pages
        </Button>
      </div>
    </div>
  );
};

//  Node Section Component
interface NodeSectionProps {
  title: string;
  nodes: any[];
  onDragStart: (event: React.DragEvent, node: any) => void;
  isDisabled: boolean;
  isAdminUser: boolean;
  showEmptyConfigFallback?: boolean;
  isCollapsible?: boolean;
  defaultOpen?: boolean;
  renderItem?: (node: any, index: number) => React.ReactNode;
}

const NodeSection: React.FC<NodeSectionProps> = ({
  title,
  nodes,
  onDragStart,
  isDisabled,
  isAdminUser,
  showEmptyConfigFallback = false,
  isCollapsible = false,
  defaultOpen = true,
  renderItem,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const navigate = useNavigate();

  const visibleNodes = React.useMemo(() => {
    return nodes.filter((node) => {
      if (!node?.permission) {
        return true;
      }

      if (node.permission === "isAdmin") {
        return isAdminUser;
      }

      if (node.permission === "isUser") {
        return !isAdminUser;
      }

      return true;
    });
  }, [nodes, isAdminUser]);

  const defaultRenderItem = (node: any, index: number) => (
    <div
      key={index}
      draggable
      onDragStart={(e) => onDragStart(e, node)}
      className={`p-2 px-3 rounded-lg bg-background text-center shadow-[0_2px_6px_rgba(0,0,0,0.12)] mb-2 text-[10px] mt-2 flex items-center justify-between gap-2 cursor-move ${isDisabled ? "shadow-[0_2px_6px_rgba(0,0,0,0.03)] text-gray-500" : "shadow-[0_2px_6px_rgba(0,0,0,0.12)]"}`}
    >
      <div className="flex items-center gap-2 font-semibold">
        {node.icon && <node.icon size={16} className={node.iconStyle} />}
        <span>{node.header}</span>
      </div>
      {node.nodeDescription && (
        <CustomTooltip text={node.nodeDescription} side="right">
          <div className="cursor-help">
            <Info
              size={12}
              className="text-muted-foreground hover:text-foreground transition"
            />
          </div>
        </CustomTooltip>
      )}
    </div>
  );

  const itemRenderer = renderItem || defaultRenderItem;

  if (visibleNodes.length === 0 && !showEmptyConfigFallback) {
    return null;
  }

  return (
    <div
      className={`px-4 py-0.5 transition-opacity duration-300 ${
        isDisabled ? "opacity-40 pointer-events-none" : "opacity-100"
      }`}
    >
      {isCollapsible ? (
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className={`w-full flex items-center justify-between text-primary uppercase font-semibold text-[10px] ${isDisabled ? "text-muted-foreground" : ""}`}
        >
          <span>{title}</span>
          <ChevronDown
            size={16}
            className={`transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      ) : (
        <label
          className={`uppercase font-semibold text-[10px]  ${isDisabled ? "text-muted-foreground" : "text-primary "}`}
        >
          {title}
        </label>
      )}

      <div className="pl-4">
        {visibleNodes.length > 0 ? (
          visibleNodes.map((node, index) => itemRenderer(node, index))
        ) : showEmptyConfigFallback ? (
          <div className="flex flex-col items-center gap-2 mt-4 border border-dashed rounded-lg px-2 py-4 bg-muted">
            <div className="flex">
              <BotOff size={16} className="text-muted-foreground mr-2" />
              <div className="text-sm text-muted-foreground text-center">
                No configurations found
              </div>
            </div>

            {/* CTA */}
            <div
              onClick={() =>
                navigate("/create-node-plugin/ui?edit=1&create-config=1")
              }
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-primaryBlue cursor-pointer bg-lightGray transition"
            >
              <PlusIcon size={16} />
              Create configuration
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default MeshflowSidebar;
